import { v4 as uuidv4 } from 'uuid';
import { API_CONFIG } from '@/config/api.config';

const API_BASE_URL = API_CONFIG.COMMERCE_API_URL;

export interface CartItem {
    cart_item_id: number;
    content_id: string;
    license_type?: string | null;
    qty: number;
    unit_price: number;
    currency: string;
    line_total: number;
    report_title?: string;
}

export interface CartResponse {
    cart_id: number;
    items: CartItem[];
    total_amount: number;
    currency: string;
}

const IMAGE_CACHE_PREFIX = 'tbrc_report_image_';

/** Strip any license suffix from a content_id to get the clean base UUID.
 *  UUIDs use hyphens, not underscores — split('_')[0] only gives the first
 *  8 chars of a UUID (e.g. "73cfd51d"), which is WRONG.
 *  This regex safely strips ONE OR MORE known suffixes like _individual_industry.
 */
const LICENSE_SUFFIX_RE = /(?:_(individual_industry|individual|industry|site|enterprise))+$/i;
function getBaseId(contentId: string): string {
    return contentId.replace(LICENSE_SUFFIX_RE, '');
}

class CartService {
    private tenantId = API_CONFIG.TENANT_ID;

    // --- Cart State Management ---
    private cartItemsCache: Set<string> = new Set(); // Stores content_ids currently in cart
    private listeners: Set<() => void> = new Set(); // Subscription callbacks
    private isSyncing = false; // Prevent concurrent syncs

    // --- Local Storage Helpers ---
    private getUserId(): string {
        if (typeof window === 'undefined') return ''; // Handle SSR

        let userId = localStorage.getItem('tbrc_user_id');
        if (!userId) {
            userId = uuidv4();
            localStorage.setItem('tbrc_user_id', userId);
        }
        return userId;
    }

    public getCartId(): number | null {
        if (typeof window === 'undefined') return null;
        const id = localStorage.getItem('tbrc_cart_id');
        return id ? parseInt(id, 10) : null;
    }

    private setCartId(id: number) {
        if (typeof window === 'undefined') return;
        localStorage.setItem('tbrc_cart_id', id.toString());
    }

    // --- Subscription Methods ---

    /**
     * Subscribe to cart changes. Returns unsubscribe function.
     */
    subscribe(callback: () => void): () => void {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    /**
     * Notify all subscribers of cart changes
     */
    private notifyListeners(): void {
        this.listeners.forEach(callback => {
            try {
                callback();
            } catch (error) {
                console.error('Error in cart listener:', error);
            }
        });
    }

    // --- Cart Status Methods ---

    /**
     * Check if an item is in the cart (by base content_id)
     * Handles license suffixes (_individual, _site, etc.)
     */
    isInCart(contentId: string | number): boolean {
        const contentIdStr = String(contentId);
        const baseId = getBaseId(contentIdStr);

        // Check if ANY variant of this content is in cart
        return Array.from(this.cartItemsCache).some(cachedId => {
            const cachedBaseId = getBaseId(cachedId);
            return cachedBaseId === baseId;
        });
    }

    /**
     * Get the number of items currently in the cart (from cache)
     */
    getCartCount(): number {
        return this.cartItemsCache.size;
    }

    async getCartItemByBaseId(baseContentId: string): Promise<CartItem | undefined> {
        try {
            const cart = await this.getCart();
            if (!cart || !cart.items) return undefined;

            // Use getBaseId() to safely strip license suffixes — NOT split('_')[0]
            // which would truncate a UUID like '73cfd51d-9326-...' to just '73cfd51d'
            return cart.items.find((item: CartItem) => {
                return getBaseId(item.content_id) === getBaseId(baseContentId);
            });
        } catch (error) {
            console.error('Error in getCartItemByBaseId:', error);
            return undefined;
        }
    }

    // --- Image Cache Methods ---

    /**
     * Store report image URL in localStorage
     */
    private setItemImage(contentId: string, imageUrl: string): void {
        if (typeof window === 'undefined' || !imageUrl) return;
        const baseId = getBaseId(contentId);
        localStorage.setItem(`${IMAGE_CACHE_PREFIX}${baseId}`, imageUrl);
    }

    /**
     * Retrieve report image URL from localStorage
     */
    getItemImage(contentId: string): string | null {
        if (typeof window === 'undefined') return null;
        const baseId = getBaseId(contentId);
        return localStorage.getItem(`${IMAGE_CACHE_PREFIX}${baseId}`);
    }

    /**
     * Sync cart state with server and update cache
     */
    async syncCart(): Promise<void> {
        if (this.isSyncing) return; // Prevent concurrent syncs

        this.isSyncing = true;
        try {
            const cart = await this.getCart();

            // Clear and rebuild cache
            this.cartItemsCache.clear();

            if (cart?.items) {
                cart.items.forEach(item => {
                    this.cartItemsCache.add(item.content_id);
                });
            }

            // Notify all subscribers
            this.notifyListeners();
        } catch (error) {
            console.error('Cart sync error:', error);
        } finally {
            this.isSyncing = false;
        }
    }

    // --- API Methods ---

    async createCart(): Promise<number> {
        const userId = this.getUserId(); // Generates or retrieves UUID
        try {
            const response = await fetch(`${API_BASE_URL}/v1/public/cart`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-ID': this.tenantId,
                    'X-Visitor-ID': userId
                }
            });

            if (!response.ok) {
                throw new Error('Failed to create cart');
            }

            const data = await response.json();
            this.setCartId(data.cart_id);
            return data.cart_id;
        } catch (error) {
            console.error('Cart creation error:', error);
            throw error;
        }
    }

    async addItem(contentId: string, unitPrice: number, currency: string = 'USD', qty: number = 1, imageUrl?: string, licenseType?: string): Promise<number> {
        // Always fetch/create cart to ensure it exists and is active
        const cartId = await this.createCart();

        const userId = this.getUserId();

        try {
            const response = await fetch(`${API_BASE_URL}/v1/public/cart/items`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-ID': this.tenantId,
                    'X-Visitor-ID': userId
                },
                body: JSON.stringify({
                    cart_id: cartId,
                    content_id: contentId,
                    license_type: licenseType ?? null,
                    qty: qty,
                    unit_price: unitPrice,
                    currency: currency
                })
            });

            if (!response.ok) {
                const err = await response.text();
                throw new Error(`Failed to add item: ${err}`);
            }

            // Store image URL in localStorage if provided
            if (imageUrl) {
                this.setItemImage(contentId, imageUrl);
            }

            // Sync cart state after adding item
            await this.syncCart();

            return cartId;
        } catch (error) {
            console.error('Add item error:', error);
            throw error;
        }
    }

    async getCart(): Promise<CartResponse | null> {
        const cartId = this.getCartId();
        const userId = this.getUserId();

        // If we have no cart ID but we have a user ID, we should try to create/fetch a cart? 
        // Or if we just want to see if there is an existing cart for this user?
        // For now, if no local cart ID, we return null.
        if (!cartId) return null;

        try {
            const response = await fetch(`${API_BASE_URL}/v1/public/cart?cart_id=${cartId}`, {
                method: 'GET',
                cache: 'no-store',
                headers: {
                    'X-Tenant-ID': this.tenantId,
                    'X-Visitor-ID': userId
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    localStorage.removeItem('tbrc_cart_id'); // Clear invalid cart
                    return null;
                }
                throw new Error('Failed to fetch cart');
            }

            return await response.json();
        } catch (error) {
            console.error('Get cart error:', error);
            return null;
        }
    }

    async removeItem(cartItemId: number): Promise<void> {
        const cartId = this.getCartId();
        if (!cartId) throw new Error('No active cart found');

        const userId = this.getUserId();

        try {
            const response = await fetch(`${API_BASE_URL}/v1/public/cart/items/${cartItemId}?cart_id=${cartId}`, {
                method: 'DELETE',
                headers: {
                    'X-Tenant-ID': this.tenantId,
                    'X-Visitor-ID': userId
                }
            });

            if (!response.ok) {
                const err = await response.text();
                throw new Error(`Failed to remove item: ${err}`);
            }

            // Sync cart state after removing item
            await this.syncCart();
        } catch (error) {
            console.error('Remove item error:', error);
            throw error;
        }
    }

    /**
     * Clear all items from the current cart
     */
    async clearCart(): Promise<void> {
        const cartId = this.getCartId();
        if (!cartId) return;

        try {
            const cart = await this.getCart();
            if (cart && cart.items) {
                // Parallel removal for better speed
                await Promise.all(cart.items.map(item => this.removeItem(item.cart_item_id)));
            }
        } catch (error) {
            console.error('Clear cart error:', error);
        }
    }
}

export const cartService = new CartService();
